import React, { forwardRef, useRef } from 'react';
import clsx from 'clsx';
import { CSSTransition } from 'react-transition-group';
import { ChipGroupContext } from '../chips-group/chip-group-transition-context';
import { RegularButton } from '../button';
import { Icon } from '../icon';
import { DelIcon } from '../icons';
import styles from './styles.module.css';
import { mergeRefs } from '../utils';
/**
 * `<Chip/>` представляет собой набор компонентов для организации Чипсов.
 *
 * - Chip - единичный Чипс.
 * - ChipGroup - обёртка для нескольких Чипсов для работы анимации.
 *
 *
 * @example ```tsx
   import * as React from 'react';
   import { Chip, ChipsGroup } from 'fronton-react';

   export const Example = () => {
    const [items, setItems] = React.useState(['example 1', 'example 2', 'example 3', 'example 4']);

    <ChipsGroup>
      {items.map(item => (
        <Chip
          key={item}
          variant="primary"
          text={item}
          onDelete={() => setItems(current => current.filter(name => name !== item))}
        />
      ))}
    </ChipsGroup>;
  };
 ```
 */
export const Chip = /*#__PURE__*/forwardRef(function Chip({
  label,
  text,
  className,
  variant = 'primary',
  onDelete,
  icon,
  title
}, ref) {
  const cssTransitionProps = React.useContext(ChipGroupContext);
  const defaultTitle = label ? `${label}: ${text}` : `${text}`;
  const actualTitle = title != null ? title : defaultTitle;
  const accessibleLabel = `Нажмите, чтобы удалить ${actualTitle}`;
  const chipRef = useRef(null);
  return /*#__PURE__*/React.createElement(CSSTransition, {
    nodeRef: chipRef,
    key: defaultTitle,
    timeout: 500,
    ...cssTransitionProps,
    onExit: () => {
      if (chipRef.current) {
        const {
          width
        } = chipRef.current.getBoundingClientRect();
        chipRef.current.style.setProperty('--__chip-last-width__', `${width}`);
        chipRef.current.style.setProperty('--__chip-last-width-unitfull__', `${width}px`);
      }
    },
    classNames: {
      exit: styles.chipTransitionExit,
      exitActive: styles.chipTransitionExitActive
    }
  }, /*#__PURE__*/React.createElement(RegularButton, {
    ref: mergeRefs([ref, chipRef]),
    rounded: true,
    size: "s",
    variant: variant,
    title: actualTitle,
    className: clsx(styles.chip, styles[variant], className),
    "aria-label": accessibleLabel,
    onClick: onDelete
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.content
  }, label ? (/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.labelWrap
  }, /*#__PURE__*/React.createElement(StartIcon, {
    icon: icon
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.label,
    "data-testid": "chip-label"
  }, label), ":\u00A0"), /*#__PURE__*/React.createElement("span", {
    className: styles.text,
    "data-testid": "chip-text"
  }, text))) : (/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StartIcon, {
    icon: icon
  }), /*#__PURE__*/React.createElement("div", {
    className: clsx(styles.text, styles.fullwidth),
    "data-testid": "chip-text"
  }, text)))), /*#__PURE__*/React.createElement("div", {
    className: styles.deleteIconWrap
  }, /*#__PURE__*/React.createElement(Icon, {
    className: styles.deleteIcon,
    size: 16,
    "data-testid": "chip-deleteicon"
  }, /*#__PURE__*/React.createElement(DelIcon, null)))));
});
const StartIcon = ({
  icon
}) => icon ? (/*#__PURE__*/React.createElement(Icon, {
  className: styles.icon,
  size: 16,
  "data-testid": "chip-starticon"
}, icon)) : null;